// tslint:disable:no-magic-numbers
import { Validations } from '../../class/workflow.type';

export interface InputStatus {
  focused?: boolean;
  selection?: [number, number];
  value: string;
}

export type InputMaskProcessingBaseConstructor = new (
  current: InputStatus,
  previous?: InputStatus,
  validations?: Validations
) => InputMaskProcessingBase;

/**
 * A base class to aid processing an input's value/selection/focus state
 * Instantiate any extending class with current and previous state of the input
 *
 * Create top level classes by extending this, and overriding `.isCharAllowed()` and `.isGroupingChar()`
 */
export abstract class InputMaskProcessingBase {
  /**
   * Expose processed value, selection start, and selection end
   */
  public readonly processed: InputStatus;

  /**
   * Value type for data storage (as opposed to rendered type which is always string)
   * Override for number or boolean
   */
  public readonly valueType: 'boolean' | 'number' | 'string' = 'string';

  /**
   * Static string of characters to allow. Will be used from `.isCharAllowed()` which can be overridden
   * Blank means any character goes
   */
  protected allowedChars: string;

  /**
   * Trigger double process to solve flexible grouping vs maxLength collision
   */
  protected doubleProcess: boolean;

  /**
   * Array of blanks and grouping caharacters in fixed positions. Override this on the mask class if fixed positions apply,
   * otherwise override `.isGroupingChar(pos)`
   * Example value for phone: ['(', , , , ')', ' ', , , , '-'] for a mask of "(xxx) xxx-xxxx"
   * NOTE: Use single characters (i.e don't use: `[..., ') ', ...]`, do use: `[..., ')', ' ', ...]`)
   */
  protected fixedPosGroupingChars: string[];

  /**
   * Optionally set a static `maxLength` for the mask (otherwise it is read from `field.config.validation.maxLength`)
   */
  protected maxLength = this.validations.maxLength;

  /**
   * Original value (as in `.current` before processing
   */
  protected readonly original: InputStatus;

  constructor(current: InputStatus, public readonly previous?: InputStatus, public readonly validations: Validations = {}) {
    current.selection = current.selection || [0, 0];
    current.value = current.value == null ? '' : String(current.value);
    this.original = current;
    this.processed = { ...current };
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/tslint/config
    this.processed.selection = <[number, number]>current.selection.slice(0, 2);
    if (previous) {
      previous.value = previous.value == null ? '' : String(previous.value);
    }
  }

  /**
   * Shorthand to check if there is any difference between the values of `.processed` and an other status object
   * @param asComparedTo comparison basis (defaults to `.original`)
   */
  public changed(asComparedTo = this.original): boolean {
    const processed = this.processed;
    return (
      processed.value !== asComparedTo.value ||
      processed.selection[0] !== asComparedTo.selection[0] ||
      processed.selection[1] !== asComparedTo.selection[1]
    );
  }

  /**
   * Inject grouping characters, filter unacceptible, move cursor etc
   * @return true if changed
   */
  public process(): boolean {
    this.preProcess();

    this.commonProcess(true);
    if (this.doubleProcess && this.changed()) {
      this.commonProcess(); // recheck, because of flexible grouping and maxLength collision
    }

    return this.changed();
  }

  /**
   * Determine if character is OK at the provided position (or should be filtered)
   * Override this for other use cases (ex: to allow `-` or `.` in certain positions)
   * @param pos 0-based index - position of the examined character of the value
   */
  protected isCharAllowed(pos: number): boolean {
    return this.allowedChars ? this.allowedChars.indexOf(this.processed.value[pos]) > -1 : true;
  }

  /**
   * Determine if character at the provided position should be an injected (aka grouping) character
   * @param pos 0-based index - position of the examined character of the value
   * @return grouping character if applicable, or falsy for non-grouping character
   */
  protected isGroupingChar(pos: number): string {
    return this.fixedPosGroupingChars && this.fixedPosGroupingChars[pos];
  }

  /**
   * Override this to have custom logic before processing
   */
  protected preProcess(): void {}

  /**
   * Override this to have custom post-processing on a mask
   */
  protected postProcess(): void {}

  /**
   * Override this to have an extra custom post-processing step for blurred fields on a mask
   */
  protected postProcessBlurred(): void {}

  /**
   * Operations for all masks
   */
  private commonProcess(firstRun?: boolean): void {
    const previous = this.previous;
    const processed = this.processed;

    // amount of characters to push. Negative value = to the left. Positive = to the right. Defaults to 1 (right).
    let pos = 0;
    const push = (insert: number): void => {
      if (pos < processed.selection[0]) {
        processed.selection[0] += insert;
      }
      if (pos < processed.selection[1]) {
        processed.selection[1] += insert;
      }
    };

    if (!previous || this.changed(previous)) {
      // deletion edge case
      if (previous && processed.value.length < previous.value.length && processed.selection[0] === previous.selection[0]) {
        let count = 0;
        while (this.isGroupingChar(processed.selection[0] + count)) {
          count++;
        }
        const remainder = processed.value.substr(processed.selection[0] + count - 1);
        if (remainder === previous.value.substr(previous.value.length - remainder.length)) {
          // if the end is identical
          processed.value = processed.value.substr(0, processed.selection[0]) + processed.value.substr(processed.selection[0] + count);
        }
      }

      // add grouping character where missing, filter invalid chars
      while (pos < processed.value.length) {
        const groupingChar = this.isGroupingChar(pos);
        if (groupingChar) {
          // grouping character position
          if (processed.value[pos] !== groupingChar) {
            // found something else
            processed.value = processed.value.substr(0, pos) + groupingChar + processed.value.substr(pos);
            push(1);
          }
        } else if (!this.isCharAllowed(pos)) {
          // filter this character
          processed.value = processed.value.substr(0, pos) + processed.value.substr(pos + 1);
          push(-1);
          continue; // skip adding to pos, since we've just removed one
        }
        pos++;
      }

      // remove trailing grouping characters
      while (this.isGroupingChar(processed.value.length - 1)) {
        processed.value = processed.value.substr(0, processed.value.length - 1);
        push(-1);
      }

      // remove excess characters
      if (this.maxLength && processed.value.length > this.maxLength) {
        push(this.maxLength - processed.value.length);
        processed.value = processed.value.substr(0, this.maxLength);
      }

      // remove excess symbols
      if (processed.value.includes(',.')) {
        push(this.maxLength - processed.value.length);
        processed.value = processed.value.replace(',.', '.');
      }

      // move cursor further if it landed on a grouping character, or got messed up (exceed value.length or end is before start)
      for (let i = 0; i <= 1; i++) {
        if (firstRun && previous) {
          if (processed.selection[i] && processed.selection[i] < previous.selection[i]) {
            while (this.isGroupingChar(processed.selection[i] - 1)) {
              processed.selection[i]--;
            }
          }
          if (processed.selection[i] > previous.selection[i]) {
            while (this.isGroupingChar(processed.selection[i])) {
              processed.selection[i]++;
            }
          }
        }
        while (processed.selection[i] > processed.value.length) {
          processed.selection[i]--;
        }
      }
      while (processed.selection[1] < processed.selection[0]) {
        processed.selection[1]++;
      }
    }

    this.postProcess();
    if (!this.processed.focused) {
      this.postProcessBlurred();
    }
  }
}
